import { useMemo, useState } from 'react'
import {
  Box, Breadcrumbs, Typography, Stack, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody, Checkbox,
} from '@mui/material'
import { Add } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  listMyExpenseReports, listMyAvailableExpenses, createExpenseReport, submitExpenseReport, getExpenseReport,
  type ExpenseReportListItem, type ExpenseReportStatus,
} from '../../api/deconturi'
import { errorMessage } from '../../api/client'

const STATUS_LABEL: Record<ExpenseReportStatus, string> = {
  Draft: 'Ciornă', Depusa: 'În aprobare', Aprobat: 'Aprobat', Respins: 'Respins', Programat: 'Programat la plată', Decontat: 'Decontat',
}
const STATUS_COLOR: Record<ExpenseReportStatus, 'default' | 'success' | 'error' | 'warning' | 'info'> = {
  Draft: 'default', Depusa: 'info', Aprobat: 'success', Respins: 'error', Programat: 'warning', Decontat: 'success',
}

export default function DeconturiMelePage() {
  const [newOpen, setNewOpen] = useState(false)
  const [detailTarget, setDetailTarget] = useState<ExpenseReportListItem | null>(null)
  const { data, isLoading } = useQuery({ queryKey: ['cheltuieli', 'deconturi-mele'], queryFn: listMyExpenseReports })

  const columns = useMemo<GridColDef<ExpenseReportListItem>[]>(() => [
    { field: 'number', headerName: 'Nr.', width: 150, renderCell: (p) => <code>{p.row.number}</code> },
    { field: 'total', headerName: 'Total', width: 110, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    {
      field: 'status', headerName: 'Status', width: 150, sortable: false,
      renderCell: (p) => <Chip size="small" label={STATUS_LABEL[p.row.status]} color={STATUS_COLOR[p.row.status]} variant="outlined" />,
    },
    { field: 'submittedAt', headerName: 'Depus la', width: 160, renderCell: (p) => p.row.submittedAt ?? '—' },
    { field: 'scheduledPaymentDate', headerName: 'Programat la', width: 130, renderCell: (p) => p.row.scheduledPaymentDate ?? '—' },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Cheltuieli</Typography>
        <Typography variant="inherit" color="text.primary">Deconturile mele</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Deconturile mele</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setNewOpen(true)}>Decont nou</Button>
      </Box>
      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          onRowClick={(p) => setDetailTarget(p.row)}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
      <NewExpenseReportDialog open={newOpen} onClose={() => setNewOpen(false)} />
      <ExpenseReportDetailDialog target={detailTarget} onClose={() => setDetailTarget(null)} />
    </Stack>
  )
}

function NewExpenseReportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const { data: available } = useQuery({ queryKey: ['cheltuieli', 'mele-disponibile'], queryFn: listMyAvailableExpenses, enabled: open })

  const mutation = useMutation({
    mutationFn: () => createExpenseReport([...selected]),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cheltuieli', 'deconturi-mele'] })
      void qc.invalidateQueries({ queryKey: ['cheltuieli', 'mele-disponibile'] })
      setSelected(new Set())
      onClose()
    },
  })

  const toggle = (id: string) => setSelected((s) => {
    const next = new Set(s)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })

  const total = (available ?? []).filter((e) => selected.has(e.id)).reduce((s, e) => s + e.amount * e.exchangeRate, 0)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Decont nou — selectați cheltuielile</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {!available?.length ? (
            <Alert severity="info">Nu aveți cheltuieli disponibile de inclus într-un decont.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow><TableCell padding="checkbox" /><TableCell>Descriere</TableCell><TableCell>Categorie</TableCell><TableCell align="right">Sumă</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {available.map((e) => (
                  <TableRow key={e.id} hover onClick={() => toggle(e.id)} sx={{ cursor: 'pointer' }}>
                    <TableCell padding="checkbox"><Checkbox checked={selected.has(e.id)} /></TableCell>
                    <TableCell>{e.description}</TableCell>
                    <TableCell>{e.categoryName}</TableCell>
                    <TableCell align="right">{e.amount.toFixed(2).replace('.', ',')} {e.currency}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Typography variant="subtitle2">Total selectat: {total.toFixed(2).replace('.', ',')} RON</Typography>
          {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Renunță</Button>
        <Button variant="contained" disabled={selected.size === 0 || mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={() => mutation.mutate()}>
          Creează decontul
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function ExpenseReportDetailDialog({ target, onClose }: { target: ExpenseReportListItem | null; onClose: () => void }) {
  const open = target !== null
  const qc = useQueryClient()
  const { data: detail } = useQuery({ queryKey: ['cheltuieli', 'decont', target?.id], queryFn: () => getExpenseReport(target!.id), enabled: open })
  const [pendingMessage, setPendingMessage] = useState<string | null>(null)

  const submitMutation = useMutation({
    mutationFn: () => submitExpenseReport(target!.id),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['cheltuieli', 'deconturi-mele'] })
      void qc.invalidateQueries({ queryKey: ['cheltuieli', 'decont', target?.id] })
      setPendingMessage(r.pendingApproval ? 'Decontul a fost trimis la aprobare.' : 'Decontul a fost procesat.')
    },
  })

  if (!open) return null

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Decont {detail?.number}</DialogTitle>
      <DialogContent>
        {!detail ? <Stack sx={{ py: 4, alignItems: 'center' }}><CircularProgress /></Stack> : (
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Chip size="small" label={STATUS_LABEL[detail.status]} color={STATUS_COLOR[detail.status]} variant="outlined" sx={{ alignSelf: 'flex-start' }} />
            <Table size="small">
              <TableHead><TableRow><TableCell>Descriere</TableCell><TableCell>Categorie</TableCell><TableCell align="right">Sumă</TableCell></TableRow></TableHead>
              <TableBody>
                {detail.lines.map((l) => (
                  <TableRow key={l.id}><TableCell>{l.description}</TableCell><TableCell>{l.categoryName}</TableCell><TableCell align="right">{l.amount.toFixed(2).replace('.', ',')} {l.currency}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography variant="body2">Total: {detail.total.toFixed(2).replace('.', ',')} RON</Typography>
            {detail.allocatedFromAdvance > 0 && <Typography variant="body2" color="text.secondary">Acoperit din avans: {detail.allocatedFromAdvance.toFixed(2).replace('.', ',')} RON</Typography>}
            {detail.remainderOwed > 0 && <Typography variant="body2" color="text.secondary">Rest de plată: {detail.remainderOwed.toFixed(2).replace('.', ',')} RON</Typography>}
            {pendingMessage && <Alert severity="info">{pendingMessage}</Alert>}
            {submitMutation.isError && <Alert severity="error">{errorMessage(submitMutation.error)}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Închide</Button>
        {detail?.status === 'Draft' && (
          <Button variant="contained" disabled={submitMutation.isPending} onClick={() => submitMutation.mutate()}>Depune decontul</Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
